/**
 * Quote Conversion Card
 *
 * Displays quote conversion funnel and metrics.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Target } from 'lucide-react';
import type { QuoteConversionFunnel } from '../types';

interface QuoteConversionCardProps {
  data?: QuoteConversionFunnel;
  loading?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function QuoteConversionCard({ data, loading }: QuoteConversionCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Quote Conversion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="animate-pulse flex gap-4">
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-20 mb-2" />
                <div className="h-8 bg-muted rounded w-16" />
              </div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-20 mb-2" />
                <div className="h-8 bg-muted rounded w-16" />
              </div>
            </div>
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-6 bg-muted rounded" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const stages = [
    { key: 'preparation', label: 'Preparation', count: data?.stages.preparation ?? 0, color: 'bg-gray-400' },
    { key: 'sent', label: 'Sent', count: data?.stages.sent ?? 0, color: 'bg-blue-400' },
    { key: 'approved', label: 'Approved', count: data?.stages.approved ?? 0, color: 'bg-green-400' },
    { key: 'invoiced', label: 'Invoiced', count: data?.stages.invoiced ?? 0, color: 'bg-green-600' },
    { key: 'rejected', label: 'Rejected', count: data?.stages.rejected ?? 0, color: 'bg-red-400' },
  ];

  const maxCount = Math.max(...stages.map((s) => s.count), 1);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Quote Conversion</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Key Metrics */}
          <div className="flex gap-4">
            <div className="flex-1 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs">Conversion Rate</span>
              </div>
              <p className="text-2xl font-bold">{data?.conversion_rate ?? 0}%</p>
            </div>
            <div className="flex-1 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Target className="h-4 w-4" />
                <span className="text-xs">Win Rate</span>
              </div>
              <p className="text-2xl font-bold">{data?.win_rate ?? 0}%</p>
            </div>
          </div>

          {/* Pipeline Value */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Approved Value</span>
              <p className="font-medium text-green-600">{formatCurrency(data?.values.approved ?? 0)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Lost Value</span>
              <p className="font-medium text-red-600">{formatCurrency(data?.values.lost ?? 0)}</p>
            </div>
          </div>

          {/* Funnel Visualization */}
          <div className="space-y-2">
            {stages.map((stage) => (
              <div key={stage.key} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-20 truncate">{stage.label}</span>
                <div className="flex-1 h-5 bg-muted rounded overflow-hidden">
                  <div
                    className={`h-full ${stage.color} flex items-center justify-end pr-2 transition-all duration-500`}
                    style={{ width: `${(stage.count / maxCount) * 100}%` }}
                  >
                    {stage.count > 0 && (
                      <span className="text-xs text-white font-medium">{stage.count}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
